import type {
  DuAnCongViecTree,
  DuAnCongViecTienDoTree,
  ItemDashboardDuAn,
} from '@/types/action-plan/DuAnView';
import type { DuAnCongViecDTO } from '@/types/action-plan/ActionPlan/DuAnCongViecDTO';
import { createMucTieuMap } from './du-an-utils';
import type { BaoCaoDuAnCongViecView } from '@/types/action-plan/ActionPlan/BaoCaoDuAnCongViecView';
import type { tabletansuat } from '@/types/performance/bangtuansuat';
import { defaultTabletansuat } from '@/types/performance/bangtuansuat';
import CommonJs from '@/utils/commonJs';
import { tabletansuatduan } from '@/types/performance/bangtuansuatduan';

/** Dòng nhóm (mục tiêu / dự án) — không gửi báo cáo, không nhập thực tế. */
export const VTN_SECTION_ID_PREFIX = '__vtn-';

export function isVtnReportSectionRow(tree: DuAnCongViecTree): boolean {
  const id = String(tree.congViec?.id ?? '');
  return id.startsWith(VTN_SECTION_ID_PREFIX);
}

/** Dòng nhóm mục tiêu (`__vtn-mt-…`) */
export function isVtnReportMucTieuSectionRow(tree: DuAnCongViecTree): boolean {
  return String(tree.congViec?.id ?? '').startsWith(`${VTN_SECTION_ID_PREFIX}mt-`);
}

/** Dòng nhóm dự án (`__vtn-da-…`) — nền nhạt hơn mục tiêu trên UI */
export function isVtnReportDuAnSectionRow(tree: DuAnCongViecTree): boolean {
  return String(tree.congViec?.id ?? '').startsWith(`${VTN_SECTION_ID_PREFIX}da-`);
}

export function isVtnReportTaskRow(tree: DuAnCongViecTree): boolean {
  const id = tree.congViec?.id;
  if (id == null || id === '') return false;
  return !isVtnReportSectionRow(tree);
}

function emptyTienDo(): DuAnCongViecTienDoTree {
  return {
    icon: null,
    titleIcon: null,
    giaiDoan: null,
    tienDoHomNay: null,
    hoanThanh: null,
    trangThai: null,
  };
}

function makeSectionCongViec(
  kind: 'mt' | 'da',
  key: string,
  title: string,
  mtctid: string
): DuAnCongViecDTO {
  const id = `${VTN_SECTION_ID_PREFIX}${kind}-${key}`;
  return {
    id,
    site: '',
    viTri: 0,
    tenCongViec: title,
    mtctid,
    isDuAn: kind === 'da',
    level: kind === 'mt' ? -1 : 0,
    stateApproved: 0,
    tanSuat: 1,
    chiTieu: 0,
    hasChild: true,
    showAlert: false,
    completeQty: 0,
    kanBanViTri: 0,
    viecDuocGiao: false,
    treePath: '',
    treePathName: '',
    isLeaf: false,
    nganSachThucHien: 0,
    kanbans: [],
  } as unknown as DuAnCongViecDTO;
}

function wrapSectionRow(
  kind: 'mt' | 'da',
  key: string,
  title: string,
  mtctid: string,
  childs: DuAnCongViecTree[]
): DuAnCongViecTree {
  return {
    congViec: makeSectionCongViec(kind, key, title, mtctid),
    hasChange: false,
    thoiGian: '',
    hasInRange: false,
    hasChild: childs.length > 0,
    param: '',
    trongKy: emptyTienDo(),
    luyke: emptyTienDo(),
    childs,
    countChilds: childs.length,
    tenTanSuat: '',
    tenHinhThucTinh: '',
    roleItemTree: {} as DuAnCongViecTree['roleItemTree'],
    tiTrongDaAn: 0,
    tiTrongTatCaDaAn: 0,
    listTanSuat: [],
  };
}

function splitTreePathLastSegment(treePathName: string): string | undefined {
  const tp = treePathName.trim();
  if (!tp) return undefined;
  const parts = tp.split(/\s*[>›|]\s*|\s*[/]\s*/).map((s) => s.trim()).filter(Boolean);
  return parts.length ? parts[parts.length - 1] : tp;
}

function prefixMucTieuTitle(label: string): string {
  const s = label.trim();
  if (!s) return 'Mục tiêu';
  return s.toLowerCase().startsWith('mục tiêu') ? s : `Mục tiêu: ${s}`;
}

type CongViecMucTieuFields = DuAnCongViecDTO & {
  tenMucTieu?: string;
  muctieuName?: string;
  TenMucTieu?: string;
};

/** Tiêu đề nhóm mục tiêu: map BSC / phuTrach / treePath / field BE — không hiển thị id. */
function mucTieuTitleFromItems(
  items: DuAnCongViecTree[],
  mtctid: string,
  nameByMtctid?: Map<string, string>
): string {
  const fromMap = nameByMtctid?.get(mtctid)?.trim();
  if (fromMap) return prefixMucTieuTitle(fromMap);

  const phuTrach = items.find((x) => x.congViec?.phuTrachMucTieu)?.congViec?.phuTrachMucTieu?.trim();
  if (phuTrach) return prefixMucTieuTitle(phuTrach);

  for (const x of items) {
    const cv = x.congViec as CongViecMucTieuFields | undefined;
    if (!cv?.treePathName) continue;
    const last = splitTreePathLastSegment(String(cv.treePathName));
    if (last) return prefixMucTieuTitle(last);
  }

  for (const x of items) {
    const cv = x.congViec as CongViecMucTieuFields | undefined;
    if (!cv) continue;
    const raw =
      (cv.tenMucTieu && String(cv.tenMucTieu).trim()) ||
      (cv.muctieuName && String(cv.muctieuName).trim()) ||
      (cv.TenMucTieu && String(cv.TenMucTieu).trim());
    if (raw) return prefixMucTieuTitle(raw);
  }

  return 'Mục tiêu';
}

/** Làm phẳng công việc dưới dự án (bỏ cấp con trong tree). */
export function flattenTaskRowsUnderNodes(nodes: DuAnCongViecTree[] | undefined): DuAnCongViecTree[] {
  if (!nodes?.length) return [];
  const out: DuAnCongViecTree[] = [];
  const walk = (arr: DuAnCongViecTree[]) => {
    for (const n of arr) {
      const cv = n.congViec;
      if (cv?.level === 0 && cv?.isDuAn) {
        if (n.childs?.length) walk(n.childs);
        continue;
      }
      out.push({ ...n, childs: [] });
      if (n.childs?.length) walk(n.childs);
    }
  };
  walk(nodes);
  return out;
}

function tasksForProject(project: DuAnCongViecTree, allInGoal: DuAnCongViecTree[]): DuAnCongViecTree[] {
  const pid = String(project.congViec?.id ?? '');
  if (project.childs?.length) {
    return flattenTaskRowsUnderNodes(project.childs);
  }
  if (!pid) return [];
  return allInGoal
    .filter((x) => {
      const cv = x.congViec;
      if (!cv) return false;
      if (cv.level === 0 && cv.isDuAn) return false;
      return String(cv.duAnID ?? '') === pid || String(cv.parentID ?? '') === pid;
    })
    .map((x) => ({ ...x, childs: [] }));
}

/**
 * Danh sách phẳng (dashboard) → cây: mục tiêu → dự án → công việc ngang cấp.
 * @param nameByMtctid map từ `listTableBSCNEW` (nếu có) để hiển thị tên mục tiêu thay vì chỉ biết id.
 */
export function buildViecTrongNgayReportTreeFromFlatList(
  flat: DuAnCongViecTree[],
  nameByMtctid?: Map<string, string>
): DuAnCongViecTree[] {
  if (!flat.length) return [];

  const grouped: Record<string, DuAnCongViecTree[]> = {};
  for (const item of flat) {
    const mt = String(item.congViec?.mtctid ?? 'unknown');
    if (!grouped[mt]) grouped[mt] = [];
    grouped[mt].push(item);
  }

  const roots: DuAnCongViecTree[] = [];
  for (const mtctid of Object.keys(grouped)) {
    const items = grouped[mtctid];
    const projects = items.filter((x) => x.congViec?.level === 0 && x.congViec?.isDuAn);

    const projectNodes: DuAnCongViecTree[] = projects.map((proj) => {
      const daTitle = proj.congViec?.tenCongViec || 'Dự án';
      const pid = String(proj.congViec?.id ?? 'idx');
      const taskChilds = tasksForProject(proj, items);
      return wrapSectionRow('da', pid, 'Dự án: ' + daTitle, mtctid, taskChilds);
    });

    const goalTitle = mucTieuTitleFromItems(items, mtctid, nameByMtctid);
    roots.push(wrapSectionRow('mt', mtctid, goalTitle, mtctid, projectNodes));
  }

  return roots;
}

/**
 * API đã trả cây mục tiêu → dự án → công việc: bọc lại bằng dòng __vtn-* (giống danh sách phẳng),
 * làm phẳng công việc dưới từng dự án.
 */
export function normalizePrebuiltVtnTree(
  roots: DuAnCongViecTree[],
  nameByMtctid?: Map<string, string>
): DuAnCongViecTree[] {
  return roots.map((goal, gi) => {
    const mtctid = String(goal.congViec?.mtctid ?? `g-${gi}`);
    const rawGoalTitle = goal.congViec?.tenCongViec?.trim() || '';
    const fromPath = goal.congViec?.treePathName?.trim() || '';
    const pathLast = fromPath ? splitTreePathLastSegment(fromPath) : '';
    const fromMap = nameByMtctid?.get(mtctid)?.trim();
    const goalTitle = rawGoalTitle
      ? prefixMucTieuTitle(rawGoalTitle)
      : fromMap
        ? prefixMucTieuTitle(fromMap)
        : pathLast
          ? prefixMucTieuTitle(pathLast)
          : 'Mục tiêu';

    const projectNodes = (goal.childs || []).map((proj, pi) => {
      const flatTasks = flattenTaskRowsUnderNodes(proj.childs || []);
      const pid = String(proj.congViec?.id ?? `p-${pi}`);
      const daTitle = proj.congViec?.tenCongViec || 'Dự án';
      return wrapSectionRow('da', pid, daTitle, mtctid, flatTasks);
    });

    return wrapSectionRow('mt', mtctid, goalTitle, mtctid, projectNodes);
  });
}

/** API dạng BSC: phương diện → `listMucTieu` (`muctieuName`, `congViecs`). */
export interface BaoCaoNhanhMucTieuNode {
  muctieuID?: string;
  muctieuName?: string;
  listKPI?: unknown[];
  congViecs?: DuAnCongViecTree[] | null;
}

export interface BaoCaoNhanhPhuongDienNode {
  phuongdienID?: string;
  phuongdienName?: string;
  listMucTieu?: BaoCaoNhanhMucTieuNode[];
  listMucTieuParent?: unknown[];
  congViecs?: DuAnCongViecTree[] | null;
}

function isPhuongDienMucTieuPayload(data: unknown): data is BaoCaoNhanhPhuongDienNode[] {
  if (!Array.isArray(data) || data.length === 0) return false;
  const first = data[0];
  if (!first || typeof first !== 'object') return false;
  return Array.isArray((first as BaoCaoNhanhPhuongDienNode).listMucTieu);
}

function looksLikeDuAnCongViecTreeRow(x: unknown): x is DuAnCongViecTree {
  return !!(x && typeof x === 'object' && 'congViec' in (x as object));
}

/**
 * `congViecs` trong một mục tiêu: trả về các nút dự án (và công việc), bỏ lớp bọc mục tiêu trùng nếu có.
 */
function projectNodesFromCongViecs(cv: DuAnCongViecTree[]): DuAnCongViecTree[] {
  if (!cv.length) return [];
  const looksNested = cv.some((g) => (g.childs?.length ?? 0) > 0);
  const subRoots = looksNested ? normalizePrebuiltVtnTree(cv) : buildViecTrongNgayReportTreeFromFlatList(cv);
  if (subRoots.length === 1 && isVtnReportMucTieuSectionRow(subRoots[0])) {
    return subRoots[0].childs ?? [];
  }
  return subRoots;
}

function buildReportTreeFromPhuongDienList(pds: BaoCaoNhanhPhuongDienNode[]): DuAnCongViecTree[] {
  const roots: DuAnCongViecTree[] = [];
  for (const pd of pds) {
    const mts = pd.listMucTieu ?? [];
    for (const mt of mts) {
      const mtctid = String(mt.muctieuID ?? 'unknown');
      const rawName = mt.muctieuName != null ? String(mt.muctieuName).trim() : '';
      const goalTitle = rawName ? prefixMucTieuTitle(rawName) : 'Mục tiêu';
      const cv = mt.congViecs;
      const projectNodes = Array.isArray(cv) && cv.length ? projectNodesFromCongViecs(cv) : [];
      roots.push(wrapSectionRow('mt', mtctid, goalTitle, mtctid, projectNodes));
    }
  }
  return roots;
}

/**
 * Chuẩn hóa payload `getBaoCaoNhanhViecTrongNgay`:
 * - `ItemDashboardDuAn` + `listDuAnCongViec`
 * - hoặc mảng phương diện có `listMucTieu` + `muctieuName`
 * - hoặc mảng `DuAnCongViecTree` thuần
 */
export function normalizeBaoCaoNhanhToReportTree(data: unknown): DuAnCongViecTree[] {
  if (data == null) return [];

  if (typeof data === 'object' && !Array.isArray(data)) {
    const raw = data as ItemDashboardDuAn;
    const list = raw.listDuAnCongViec ?? [];
    if (list.length) {
      const nameByMtctid = createMucTieuMap(raw.listTableBSCNEW);
      const looksNested = list.some((g) => (g.childs?.length ?? 0) > 0);
      if (looksNested) return normalizePrebuiltVtnTree(list, nameByMtctid);
      return buildViecTrongNgayReportTreeFromFlatList(list, nameByMtctid);
    }
  }

  if (Array.isArray(data)) {
    if (data.length && isPhuongDienMucTieuPayload(data)) {
      return buildReportTreeFromPhuongDienList(data);
    }
    if (data.length && looksLikeDuAnCongViecTreeRow(data[0])) {
      const list = data as DuAnCongViecTree[];
      const looksNested = list.some((g) => (g.childs?.length ?? 0) > 0);
      if (looksNested) return normalizePrebuiltVtnTree(list);
      return buildViecTrongNgayReportTreeFromFlatList(list);
    }
  }

  return [];
}

/** Bản hiển thị bảng: giữ `childs` (đồng bộ state), thêm `children` cho Ant Design Table. */
export type VtnReportTableRow = DuAnCongViecTree & { children?: VtnReportTableRow[] };

export function mapChildsToChildrenForTable(nodes: DuAnCongViecTree[]): VtnReportTableRow[] {
  return nodes.map((n) => ({
    ...n,
    children: n.childs?.length ? mapChildsToChildrenForTable(n.childs) : undefined,
  }));
}

export function flattenVtnReportTasks(roots: DuAnCongViecTree[]): DuAnCongViecTree[] {
  const out: DuAnCongViecTree[] = [];
  const walk = (arr: DuAnCongViecTree[]) => {
    for (const n of arr) {
      if (isVtnReportTaskRow(n)) out.push(n);
      if (n.childs?.length) walk(n.childs);
    }
  };
  walk(roots);
  return out;
}

/** Cập nhật listTanSuat tại một công việc (tìm theo id trong cây). */
export function patchVtnTreeTaskThucTe(
  roots: DuAnCongViecTree[],
  congViecId: string,
  periodIndex: number,
  value: number | null
): DuAnCongViecTree[] {
  const patchNode = (n: DuAnCongViecTree): DuAnCongViecTree => {
    const childs = n.childs?.map(patchNode);
    if (String(n.congViec?.id ?? '') !== congViecId) {
      return childs ? { ...n, childs } : n;
    }
    const lt = [...(n.listTanSuat || [])];
    if (!lt[periodIndex]) return childs ? { ...n, childs } : n;
    lt[periodIndex] = { ...lt[periodIndex], thucTe: value };
    return { ...n, listTanSuat: lt, ...(childs ? { childs } : {}) };
  };
  return roots.map(patchNode);
}



/** Chuẩn hóa để so sánh thực tế đã nhập / từ server */
export function normThucTe(v: unknown): string | null {
  if (v === null || v === undefined || v === '') return null;
  const n = Number(v);
  if (Number.isNaN(n)) return String(v);
  return String(n);
}

export type ThucTeSnapshot = Record<string, Record<string, string | null>>;

export function snapshotThucTeByTask(list: DuAnCongViecTree[]): ThucTeSnapshot {
  const snap: ThucTeSnapshot = {};
  for (const t of flattenVtnReportTasks(list)) {
    const id = t.congViec?.id || String(t.idTemp ?? '');
    if (!id) continue;
    const row: Record<string, string | null> = {};
    (t.listTanSuat || []).forEach((item, idx) => {
      const key = (item.idbaocao && String(item.idbaocao)) || `idx:${idx}`;
      row[key] = normThucTe(item.thucTe);
    });
    snap[id] = row;
  }
  return snap;
}

export function listHasDirtyThucTe(list: DuAnCongViecTree[], snap: ThucTeSnapshot): boolean {
  for (const t of flattenVtnReportTasks(list)) {
    const id = t.congViec?.id || String(t.idTemp ?? '');
    const rowSnap = snap[id] || {};
    const items = t.listTanSuat || [];
    for (let idx = 0; idx < items.length; idx++) {
      const item = items[idx];
      const key = (item.idbaocao && String(item.idbaocao)) || `idx:${idx}`;
      if (normThucTe(item.thucTe) !== (rowSnap[key] ?? null)) return true;
    }
  }
  return false;
}

export function mergeTanSuatForBaoCaoApi(item: Partial<tabletansuat>): tabletansuat {
  const th =
    item.thucTe === null || item.thucTe === undefined || item.thucTe === ('' as any)
      ? null
      : Number(item.thucTe);
  const chi = item.chiTieu ?? defaultTabletansuat.chiTieu ?? 0;
  return {
    ...defaultTabletansuat,
    ...(item as object),
    idbaocao: item.idbaocao || (item as any).id || '',
    thucTe: th,
    thucTeHienThi:
      th == null || Number.isNaN(Number(th))
        ? ''
        : Number(th).toLocaleString('vi-VN', { maximumFractionDigits: 3 }),
    danhGia: (item.danhGia as string) ?? '',
    filedinhkem: (item.filedinhkem as string) ?? '',
    trangThai: th != null && !Number.isNaN(Number(th)) && Number(th) >= Number(chi),
    thucTeBieuDo: th == null || Number.isNaN(Number(th)) ? 0 : Number(th),
  } as tabletansuat;
}

export type BuildBaoCaoPayloadOptions = {
  /** BE [Required] — tên đơn vị đang chọn (cây bộ phận / header). */
  tenDonVi: string;
};

/** BE yêu cầu `Kanbans` không null; payload từ getviectrongngay thường có `kanbans: null`. */
export function normalizeCongViecForBaoCaoApi(cv: DuAnCongViecDTO | undefined): DuAnCongViecDTO {
  const base = (cv || {}) as DuAnCongViecDTO;
  return {
    ...base,
    kanbans: Array.isArray(base.kanbans) ? base.kanbans : [],
    treePath: base.treePath ?? '',
    treePathName: base.treePathName ?? '',
    isLeaf: base.isLeaf ?? false,
    nganSachThucHien: base.nganSachThucHien ?? 0,
    level: base.level ?? 0,
  } as DuAnCongViecDTO;
}

/** Mỗi công việc một view; `listTanSuat` chỉ gồm các kỳ có `thucTe` khác snapshot (không gửi toàn bộ). */
export function buildDirtyBaoCaoPayloads(
  list: DuAnCongViecTree[],
  snap: ThucTeSnapshot,
  options: BuildBaoCaoPayloadOptions
): BaoCaoDuAnCongViecView[] {
  const tenDonVi = String(options.tenDonVi || '').trim() || '—';
  const out: BaoCaoDuAnCongViecView[] = [];
  for (const tree of flattenVtnReportTasks(list)) {
    const id = tree.congViec?.id;
    if (!id) continue;
    const rowSnap = snap[id] || {};
    const changed: tabletansuat[] = [];
    (tree.listTanSuat || []).forEach((item, idx) => {
      const key = (item.idbaocao && String(item.idbaocao)) || `idx:${idx}`;
      const now = normThucTe(item.thucTe);
      const was = rowSnap[key] ?? null;
      if (now !== was) {
        changed.push(mergeTanSuatForBaoCaoApi(item));
      }
    });
    if (changed.length) {
      out.push({
        congViec: normalizeCongViecForBaoCaoApi(tree.congViec as DuAnCongViecDTO),
        listTanSuat: changed,
        listNganSachs: [],
        dicTienDos: {},
        tenDonVi,
      });
    }
  }
  return out;
}


/** 1: Ngày, 2: Tuần, 3: Tháng, 4: Báo cáo cuối kỳ */
export function getTanSuatLabel(n?: number | null): string {
  switch (n) {
    case 1:
      return 'Ngày';
    case 2:
      return 'Tuần';
    case 3:
      return 'Tháng';
    case 4:
      return 'Báo cáo cuối kỳ';
    default:
      return '—';
  }
}

export function tanSuatBadgeProps(n?: number | null): { color: string } {
  return { color: 'default' };
  // switch (n) {
  //   case 1:
  //     return { color: 'blue' };
  //   case 2:
  //     return { color: 'cyan' };
  //   case 3:
  //     return { color: 'geekblue' };
  //   case 4:
  //     return { color: 'purple' };
  //   default:
  //     return { color: 'default' };
  // }
}

/** Khớp ViecTrongNgay index — chỉ `stateApproved === 1` mới nhập lưới báo cáo. */
export function rowBlocksPeriodInput(tree: DuAnCongViecTree): boolean {
  if (isVtnReportSectionRow(tree)) return true;
  return tree.congViec?.stateApproved !== 1;
}

/** Nhãn trạng thái duyệt — dùng cho tooltip khi khóa nhập báo cáo. */
export function getStateApprovedLabel(stateApproved?: number | null): string {
  if (stateApproved === 0) return 'Chờ xác nhận';
  if (stateApproved === 1) return 'Đã xác nhận';
  if (stateApproved === 2) return 'Từ chối';
  return 'Chờ xác nhận';
}

/** Ô nhập thực tế — viền/shadow rõ, số và placeholder căn giữa. */
export const INPUT_NUMBER_PROMINENT: React.CSSProperties = {
  borderRadius: 8,
  fontWeight: 600,
  textAlign: 'center',
  border: '1px solid #64748b',
  boxShadow: '0 1px 3px rgba(15, 23, 42, 0.12)',
  background: '#fff',
};

export function formatThoiGian(tree: DuAnCongViecTree): string {
  if (tree.thoiGian && String(tree.thoiGian).trim()) return String(tree.thoiGian);
  const cv = tree.congViec;
  if (cv?.ngayBatDau && cv?.ngayKetThuc) {
    const dateStart = new Date(cv.ngayBatDau);
    const dateEnd = new Date(cv.ngayKetThuc);

    if (isNaN(dateStart.getTime()) || isNaN(dateEnd.getTime())) return '—';

    const dayStart = dateStart.getDate();
    const monthStart = dateStart.getMonth() + 1;
    const yearStart = dateStart.getFullYear();

    const dayEnd = dateEnd.getDate();
    const monthEnd = dateEnd.getMonth() + 1;
    const yearEnd = dateEnd.getFullYear();

    // Nếu cùng năm
    if (yearStart === yearEnd) {
      // Nếu cùng tháng
      if (monthStart === monthEnd) {
        return `${dayStart} - ${dayEnd}/${monthStart}`;
      }
      // Khác tháng nhưng cùng năm
      return `${dayStart}/${monthStart} - ${dayEnd}/${monthEnd}`;
    }

    // Khác năm
    return `${dayStart}/${monthStart}/${yearStart} - ${dayEnd}/${monthEnd}/${yearEnd}`;
  }
  return '—';
}

export function sumThucTe(list?: Partial<tabletansuatduan>[] | null): number {
  return (list || []).reduce((s, x) => s + (Number(x.thucTe) || 0), 0);
}

export function toDayKey(input: string | Date | null | undefined): string | null {
  if (input == null || input === '') return null;
  const d = input instanceof Date ? input : new Date(input);
  if (Number.isNaN(d.getTime())) return null;
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, '0');
  const day = String(d.getDate()).padStart(2, '0');
  return `${y}-${m}-${day}`;
}

/**
 * Parse kỳ filter dạng "DD/MM/YYYY - DD/MM/YYYY" (ví dụ tháng đã chọn trên trang báo cáo)
 * → cận so sánh YYYY-MM-DD. Chuỗi khác / không parse được → null.
 */
export function parseDateChoiceRangeWindow(anchorDate?: string | null): { start: string; end: string } | null {
  const raw = anchorDate && String(anchorDate).trim();
  if (!raw) return null;
  const parts = raw.split(' - ').map((s) => s.trim()).filter(Boolean);
  if (parts.length !== 2) return null;

  const parseViDate = (s: string): string | null => {
    const seg = s.split('/');
    if (seg.length !== 3) return null;
    const d = parseInt(seg[0], 10);
    const mo = parseInt(seg[1], 10);
    const y = parseInt(seg[2], 10);
    if (!Number.isFinite(d) || !Number.isFinite(mo) || !Number.isFinite(y)) return null;
    if (mo < 1 || mo > 12) return null;
    const dim = new Date(y, mo, 0).getDate();
    if (d < 1 || d > dim) return null;
    return toDayKey(new Date(y, mo - 1, d));
  };

  const a = parseViDate(parts[0]);
  const b = parseViDate(parts[1]);
  if (!a || !b) return null;
  return a <= b ? { start: a, end: b } : { start: b, end: a };
}

export function parseYMD(s: string): Date {
  const [y, m, d] = s.split('-').map(Number);
  return new Date(y, m - 1, d);
}

/** Ngày YYYY-MM-DD từ `ketthuc` (ngày chốt hiển thị ô nhập), không có / lỗi → null. */
export function itemKetThucDayKey(item: Partial<tabletansuatduan>): string | null {
  if (item.ketthuc == null || item.ketthuc === '') return null;
  const b = new Date(item.ketthuc as any);
  if (Number.isNaN(b.getTime())) return null;
  return toDayKey(b);
}

export function itemDayRange(item: Partial<tabletansuatduan>): { start: string; end: string } | null {
  const a =
    item.batdau != null && item.batdau !== ''
      ? new Date(item.batdau as any)
      : null;
  const b =
    item.ketthuc != null && item.ketthuc !== ''
      ? new Date(item.ketthuc as any)
      : null;
  if (a && !Number.isNaN(a.getTime()) && b && !Number.isNaN(b.getTime())) {
    const s = toDayKey(a);
    const e = toDayKey(b);
    if (s && e) return s <= e ? { start: s, end: e } : { start: e, end: s };
  }
  // Chỉ có ngày kết thúc kỳ — vẫn gắn kỳ vào đúng một cột lịch
  if ((!a || Number.isNaN(a.getTime())) && b && !Number.isNaN(b.getTime())) {
    const e = toDayKey(b);
    if (e) return { start: e, end: e };
  }
  const tu = item.tungay && String(item.tungay).trim();
  if (tu) {
    const k = toDayKey(tu);
    if (k) return { start: k, end: k };
  }
  return null;
}

export function enumerateDays(start: string, end: string): string[] {
  const out: string[] = [];
  let cur = parseYMD(start);
  const endD = parseYMD(end);
  while (cur <= endD) {
    const k = toDayKey(cur);
    if (k) out.push(k);
    cur = new Date(cur);
    cur.setDate(cur.getDate() + 1);
  }
  return out;
}

export  function collectGlobalDayRange(grid: DuAnCongViecTree[]): { start: string; end: string } | null {
  let min: string | null = null;
  let max: string | null = null;
  const bump = (s: string, e: string) => {
    const [a, b] = s <= e ? [s, e] : [e, s];
    if (!min || a < min) min = a;
    if (!max || b > max) max = b;
  };
  for (const row of flattenVtnReportTasks(grid)) {
    for (const item of row.listTanSuat || []) {
      const r = itemDayRange(item);
      if (r) bump(r.start, r.end);
    }
    const cv = row.congViec;
    if (cv?.ngayBatDau && cv?.ngayKetThuc) {
      const s = toDayKey(cv.ngayBatDau as any);
      const e = toDayKey(cv.ngayKetThuc as any);
      if (s && e) bump(s, e);
    }
  }
  if (!min || !max) return null;
  return { start: min, end: max };
}

export function clipDayList(days: string[], anchor: string | undefined, maxDays: number): string[] {
  if (days.length <= maxDays) return days;
  let idx = 0;
  if (anchor) {
    const exact = days.indexOf(anchor);
    if (exact >= 0) idx = exact;
    else {
      const t = parseYMD(anchor).getTime();
      let best = 0;
      let bestDiff = Infinity;
      days.forEach((d, i) => {
        const diff = Math.abs(parseYMD(d).getTime() - t);
        if (diff < bestDiff) {
          bestDiff = diff;
          best = i;
        }
      });
      idx = best;
    }
  } else {
    idx = Math.floor(days.length / 2);
  }
  const half = Math.floor(maxDays / 2);
  const start = Math.max(0, idx - half);
  return days.slice(start, Math.min(days.length, start + maxDays));
}

export function formatShortDayVi(dayKey: string): string {
  const d = parseYMD(dayKey);
  return `${String(d.getDate()).padStart(2, '0')}/${String(d.getMonth() + 1).padStart(2, '0')}`;
}

export function monthTitleVi(dayKey: string): string {
  const d = parseYMD(dayKey);
  return `Tháng ${d.getMonth() + 1}/${d.getFullYear()}`;
}

type WeekChunk = { key: string; weekLabel: string; days: string[] };
type MonthChunk = { key: string; monthLabel: string; weeks: WeekChunk[] };

/** Nhóm theo tháng dương lịch; trong mỗi tháng chia lô 7 ngày (tuần hiển thị). */
export function partitionMonthAndWeeks(dayList: string[]): MonthChunk[] {
  if (!dayList.length) return [];
  const byMonth = new Map<string, string[]>();
  for (const dk of dayList) {
    const d = parseYMD(dk);
    const mk = `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}`;
    if (!byMonth.has(mk)) byMonth.set(mk, []);
    byMonth.get(mk)!.push(dk);
  }
  const months = [...byMonth.entries()].sort(([a], [b]) => a.localeCompare(b));
  return months.map(([mk, days]) => {
    const weeks: WeekChunk[] = [];
    for (let i = 0; i < days.length; i += 7) {
      const chunk = days.slice(i, i + 7);
      weeks.push({
        key: `w-${chunk[0]}`,
        weekLabel: `Tuần ${weeks.length + 1}`,
        days: chunk,
      });
    }
    return {
      key: mk,
      monthLabel: monthTitleVi(days[0]),
      weeks,
    };
  });
}

/** Gộp các tuần của nhiều tháng thành một dãy cột (bỏ hàng header Tháng). */
export function flattenWeeksForHeader(months: MonthChunk[]): WeekChunk[] {
  const out: WeekChunk[] = [];
  let w = 0;
  for (const m of months) {
    for (const week of m.weeks) {
      w += 1;
      out.push({
        ...week,
        weekLabel: `Tuần ${w}`,
      });
    }
  }
  return out;
}

export function gatherColumnPeriodItemsByDay(grid: DuAnCongViecTree[], dayKey: string) {
  const out: { item: Partial<tabletansuatduan>; row: DuAnCongViecTree }[] = [];
  for (const r of flattenVtnReportTasks(grid)) {
    for (const item of r.listTanSuat || []) {
      const range = itemDayRange(item);
      if (!range) continue;
      const kt = itemKetThucDayKey(item);
      if (kt) {
        if (dayKey !== kt) continue;
      } else if (dayKey < range.start || dayKey > range.end) {
        continue;
      }
      out.push({ item, row: r });
    }
  }
  return out;
}

export function columnTanSuatCodesForDay(grid: DuAnCongViecTree[], dayKey: string): number[] {
  const raw: number[] = [];
  for (const { item, row } of gatherColumnPeriodItemsByDay(grid, dayKey)) {
    const fromItem = (item as any).tanSuat;
    if (fromItem != null && fromItem !== '') {
      const n = Number(fromItem);
      if (!Number.isNaN(n)) raw.push(n);
    } else if (row.congViec?.tanSuat != null) {
      raw.push(Number(row.congViec.tanSuat));
    }
  }
  return [...new Set(raw)].sort((a, b) => a - b);
}

export function columnHeaderDateHintForDay(grid: DuAnCongViecTree[], dayKey: string): string | null {
  const hints = new Set<string>();
  for (const { item } of gatherColumnPeriodItemsByDay(grid, dayKey)) {
    const tu = item.tungay && String(item.tungay).trim();
    if (tu) {
      hints.add(tu);
      continue;
    }
    const a =
      item.batdau != null && item.batdau !== ''
        ? CommonJs.formatDate(new Date(item.batdau as any))
        : '';
    const b =
      item.ketthuc != null && item.ketthuc !== ''
        ? CommonJs.formatDate(new Date(item.ketthuc as any))
        : '';
    if (a && b) hints.add(`${a} – ${b}`);
    else if (a || b) hints.add(a || b);
  }
  if (hints.size === 1) return [...hints][0];
  return null;
}

export function dayColumnCurrent(grid: DuAnCongViecTree[], dayKey: string): boolean {
  for (const { item } of gatherColumnPeriodItemsByDay(grid, dayKey)) {
    if ((item as any).current) return true;
  }
  return false;
}

/**
 * Ô nhập báo cáo theo ngày:
 * - Có `ketthuc` → chỉ hiện đúng cột ngày kết thúc kỳ (date end).
 * - Không có `ketthuc` → như cũ: ô đầu tiên của kỳ trong vùng hiển thị; các ngày sau cùng kỳ → colSpan 0.
 */
export function resolvePeriodCell(
  row: DuAnCongViecTree,
  dayKey: string,
  visibleDays: string[]
): { colSpan: number; periodIndex: number; item: Partial<tabletansuatduan>; spanCurrent: boolean; placeInputAtEnd: boolean } | { colSpan: 0 } | null {
  const list = row.listTanSuat || [];
  for (let pi = 0; pi < list.length; pi++) {
    const item = list[pi];
    const range = itemDayRange(item);
    if (!range) continue;

    const ketThucKey = itemKetThucDayKey(item);
    if (ketThucKey) {
      if (dayKey !== ketThucKey) continue;
      if (!visibleDays.includes(dayKey)) continue;

      const tanSuatRaw = (item as any)?.tanSuat ?? row.congViec?.tanSuat;
      const tanSuatCode = Number(tanSuatRaw);
      const placeInputAtEnd = tanSuatCode === 4;

      return {
        colSpan: 1,
        periodIndex: pi,
        item,
        spanCurrent: !!(item as any).current,
        placeInputAtEnd,
      };
    }

    if (dayKey < range.start || dayKey > range.end) continue;

    const daysInRangeVisible = visibleDays.filter((d) => d >= range.start && d <= range.end);
    if (!daysInRangeVisible.length) continue;

    if (dayKey !== daysInRangeVisible[0]) {
      return { colSpan: 0 };
    }

    const tanSuatRaw = (item as any)?.tanSuat ?? row.congViec?.tanSuat;
    const tanSuatCode = Number(tanSuatRaw);
    const placeInputAtEnd = tanSuatCode === 4;

    return {
      colSpan: daysInRangeVisible.length,
      periodIndex: pi,
      item,
      spanCurrent: !!(item as any).current,
      placeInputAtEnd,
    };
  }
  return null;
}

/** Fallback: cột theo index khi không parse được ngày */
export function gatherColumnPeriodItems(grid: DuAnCongViecTree[], colIndex: number): { item: Partial<tabletansuatduan>; row: DuAnCongViecTree }[] {
  const out: { item: Partial<tabletansuatduan>; row: DuAnCongViecTree }[] = [];
  for (const r of flattenVtnReportTasks(grid)) {
    const item = r.listTanSuat?.[colIndex];
    if (item) out.push({ item, row: r });
  }
  return out;
}

export function columnTanSuatCodes(grid: DuAnCongViecTree[], colIndex: number): number[] {
  const raw: number[] = [];
  for (const { item, row } of gatherColumnPeriodItems(grid, colIndex)) {
    const fromItem = (item as any).tanSuat;
    if (fromItem != null && fromItem !== '') {
      const n = Number(fromItem);
      if (!Number.isNaN(n)) raw.push(n);
    } else if (row.congViec?.tanSuat != null) {
      raw.push(Number(row.congViec.tanSuat));
    }
  }
  return [...new Set(raw)].sort((a, b) => a - b);
}

export function columnHeaderTitle(grid: DuAnCongViecTree[], colIndex: number): string {
  const titles = new Set<string>();
  for (const { item } of gatherColumnPeriodItems(grid, colIndex)) {
    const t =
      (item.title && String(item.title).trim()) ||
      (item.baocaochoky && String(item.baocaochoky).trim());
    if (t) titles.add(t);
  }
  if (titles.size === 0) return `T${colIndex + 1}`;
  if (titles.size === 1) return [...titles][0];
  const arr = [...titles];
  return arr.length <= 2 ? arr.join(' / ') : `${arr[0]} / ${arr[1]}…`;
}

export function columnHeaderDateHint(grid: DuAnCongViecTree[], colIndex: number): string | null {
  const hints = new Set<string>();
  for (const { item } of gatherColumnPeriodItems(grid, colIndex)) {
    const tu = item.tungay && String(item.tungay).trim();
    if (tu) {
      hints.add(tu);
      continue;
    }
    const a =
      item.batdau != null && item.batdau !== ''
        ? CommonJs.formatDate(new Date(item.batdau as any))
        : '';
    const b =
      item.ketthuc != null && item.ketthuc !== ''
        ? CommonJs.formatDate(new Date(item.ketthuc as any))
        : '';
    if (a && b) hints.add(`${a} – ${b}`);
    else if (a || b) hints.add(a || b);
  }
  if (hints.size === 1) return [...hints][0];
  return null;
}

